// Céu e luz do Campus: a hora que o escritório mostra (com ?hora=21:30 para testar qualquer hora),
// o sol que entra pelas janelas, o tom do céu no amanhecer, no entardecer e à noite, os faróis dos
// carros, os vaga-lumes do jardim e o enfeite da época nos personagens (gorro e chapéu).
//
// Tudo aqui é puro (sem DOM): os testes rodam em Node. O desenho fica em ceu-desenho.ts.

import { stableHash } from '../../core/hash.js';
import type { RoomMap } from '../../core/world.js';
import { CHAPEU_BRUXA } from '../art/fantasia.js';
import { FURNITURE_BY_ID } from '../art/furniture/index.js';
import { GORRO_NATAL } from '../art/gorro.js';
import { SEASONS, seasonOf, type Season } from '../art/seasonal.js';
import type { PixelSprite } from '../art/types.js';

// ------------------------------------------------------------------ hora

/** Hora de ?hora=HH:MM em decimal (21:30 → 21.5). Fora do formato ou da faixa, null. */
export function horaDaUrl(search: string): number | null {
  const valor = new URLSearchParams(search).get('hora') ?? '';
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(valor);
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60;
}

/** A hora que o céu usa: a da URL, se houver, senão a do relógio. */
export function horaAgora(agora: Date, search: string): number {
  return horaDaUrl(search) ?? agora.getHours() + agora.getMinutes() / 60;
}

/** Tema da época: ?tema= força (para ver fora da data), depois o ajuste, depois o calendário. */
export function temaEfetivo(search: string, ajuste: string | undefined, agora: Date): Season {
  const forcado = new URLSearchParams(search).get('tema');
  if (SEASONS.includes(forcado as Season)) return forcado as Season;
  return ajuste && ajuste !== 'auto' ? (ajuste as Season) : seasonOf(agora);
}

/** Enfeite da cabeça dos personagens na época: gorro no Natal, chapéu de bruxa no Halloween. */
export function chapeuDaEpoca(tema: Season): PixelSprite | null {
  if (tema === 'natal') return GORRO_NATAL;
  if (tema === 'halloween') return CHAPEU_BRUXA;
  return null;
}

// ------------------------------------------------------------------ sol

/**
 * Altura do sol de 0 (nascer e pôr, ou de noite) a 1 (meio-dia). Senoide entre 6h e 18h, que
 * dá a curva do dia: o sol sobe devagar de manhã e desce devagar à tarde.
 */
export function alturaDoSol(hora: number): number {
  if (hora <= 6 || hora >= 18) return 0;
  return Math.sin((Math.PI * (hora - 6)) / 12);
}

export interface RaioDoSol {
  /** Quanto a luz vai do peitoril até o chão, em px. Curta ao meio-dia, longa no fim da tarde. */
  comprimento: number;
  /** Quanto o feixe escorrega para o lado, em px. O sol a oeste (tarde) empurra a luz para o leste. */
  deslocamento: number;
  /** Opacidade do feixe no peitoril (0 a 1). */
  alfa: number;
  /** Cor do feixe: amarelo-branco ao meio-dia, laranja ao fim da tarde. */
  cor: readonly [number, number, number];
}

/** O raio de sol da hora, ou null quando o sol não está no céu (noite). */
export function raioDoSol(hora: number): RaioDoSol | null {
  const altura = alturaDoSol(hora);
  if (altura <= 0.02) return null;
  const baixo = 1 - altura; // 0 ao meio-dia, perto de 1 no nascer e no pôr
  const comprimento = Math.round(12 + 72 * baixo);
  const lado = Math.max(-1, Math.min(1, (hora - 12) / 6));
  return {
    comprimento,
    deslocamento: Math.round(lado * comprimento * 0.5),
    alfa: 0.3 + 0.2 * altura,
    cor: [255, Math.round(150 + 100 * altura), Math.round(60 + 160 * altura)],
  };
}

/** Janelas da planta (parede norte, y = 0): canto esquerdo em tiles e largura. */
export function janelasDaSala(map: RoomMap): Array<{ x: number; y: number; w: number }> {
  const w = FURNITURE_BY_ID.get('janela')?.footprint.w ?? 2;
  return map.items.filter((p) => p.item === 'janela').map((p) => ({ x: p.x, y: p.y, w }));
}

/**
 * Feixes que uma janela joga no chão: um halo largo e fraco e um miolo mais forte, por dentro.
 * (x0, x1) é a largura da janela em px, relativa à origem do piso; os pontos saem do peitoril (y = 0).
 */
export function feixesDaJanela(x0: number, x1: number, raio: RaioDoSol): Array<{ pontos: Array<[number, number]>; fator: number }> {
  const { comprimento: c, deslocamento: d } = raio;
  const feixe = (a: number, b: number, abre: number) => [
    [a, 0],
    [b, 0],
    [b + d + c * abre, c],
    [a + d - c * abre, c],
  ] as Array<[number, number]>;
  return [
    { pontos: feixe(x0, x1, 0.16), fator: 0.45 },
    { pontos: feixe(x0 + 4, x1 - 4, 0.05), fator: 1 },
  ];
}

// ------------------------------------------------------------------ céu

/** Pontos do céu (hora, r, g, b, alfa). Entre dois pontos, a cor passa aos poucos, sem degrau. */
const CEU: ReadonlyArray<readonly [number, number, number, number, number]> = [
  [0, 10, 14, 44, 0.5],
  [5, 10, 14, 44, 0.5],
  [6.5, 255, 170, 120, 0.16],
  [7.5, 255, 190, 150, 0],
  [16.5, 255, 190, 150, 0],
  [17.5, 255, 128, 64, 0.22],
  [18.5, 150, 60, 80, 0.4],
  [19.5, 10, 14, 44, 0.5],
  [24, 10, 14, 44, 0.5],
];

/** Véu do céu na hora dada (amanhecer e entardecer quentes, noite azulada); null de dia. */
export function corDoCeu(hora: number): string | null {
  const h = ((hora % 24) + 24) % 24;
  for (let i = 0; i < CEU.length - 1; i++) {
    const a = CEU[i]!;
    const b = CEU[i + 1]!;
    if (h < a[0] || h > b[0]) continue;
    const t = (h - a[0]) / (b[0] - a[0]);
    const v = [1, 2, 3, 4].map((k) => a[k]! + (b[k]! - a[k]!) * t);
    if (v[3]! < 0.005) return null;
    return `rgba(${Math.round(v[0]!)}, ${Math.round(v[1]!)}, ${Math.round(v[2]!)}, ${v[3]!.toFixed(3)})`;
  }
  return null;
}

// ------------------------------------------------------------------ vaga-lumes e faróis

export interface Vagalume {
  /** Posição de repouso, em px de arte. */
  x: number;
  y: number;
  /** Fase do passeio e do pisca-pisca (rad). */
  fase: number;
}

/**
 * Vaga-lumes no jardim: pontos espalhados por hash estável na área (px), fora das caixas `evitar`
 * (prédios). Mesma posição em toda carga da página.
 */
export function vagalumesNoJardim(largura: number, altura: number, evitar: readonly { x: number; y: number; w: number; h: number }[], quantos: number): Vagalume[] {
  const out: Vagalume[] = [];
  for (let i = 0; out.length < quantos && i < quantos * 12; i++) {
    const x = ((stableHash(`vx${i}`) % 10000) / 10000) * largura;
    const y = ((stableHash(`vy${i}`) % 10000) / 10000) * altura;
    if (evitar.some((r) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h)) continue;
    out.push({ x, y, fase: ((stableHash(`vf${i}`) % 628) / 100) });
  }
  return out;
}

/**
 * Onde e quão aceso está o vaga-lume agora. Com movimento reduzido (prefers-reduced-motion) ele
 * fica parado e com brilho constante.
 */
export function posicaoDoVagalume(v: Vagalume, agora: number, reduzido: boolean): { x: number; y: number; brilho: number } {
  if (reduzido) return { x: v.x, y: v.y, brilho: 0.8 };
  const x = v.x + Math.sin(agora / 1700 + v.fase) * 10;
  const y = v.y + Math.cos(agora / 2300 + v.fase * 1.7) * 6;
  const pisca = Math.max(0, Math.sin(agora / 520 + v.fase * 3));
  return { x, y, brilho: 0.15 + 0.85 * pisca * pisca };
}

/**
 * Os dois faróis da frente de um carro (px). O carro anda para a direita (a frente é o lado
 * direito) ou, com `paraEsquerda`, para a esquerda. (x, y) é o canto de cima da arte, como ela
 * é desenhada em campus.ts (de cima para baixo: y + h é a base).
 */
export function faroisDoCarro(x: number, y: number, w: number, h: number, paraEsquerda: boolean): Array<{ x: number; y: number }> {
  const frente = paraEsquerda ? x + 2 : x + w - 2;
  return [
    { x: frente, y: y + h * 0.45 },
    { x: frente, y: y + h * 0.7 },
  ];
}
